// frontend/src/pages/ManageSharesPage.jsx
import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import AdminLayout from "./AdminLayout";
import { useNavigate, useLocation } from "react-router-dom";
import { writeActivityLog } from "../utils/activityLog";
import globalApi from "../utils/globalFetchWrapper";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const fmt = (v, dp = 0) =>
  Number(v || 0).toLocaleString(undefined, {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  });

// ── Searchable member picker ─────────────────────────────────────────────────
// Uses position:fixed calculated from getBoundingClientRect so it floats
// completely outside any overflow:hidden ancestor — no clipping possible.
function MemberSearch({ members, value, onChange }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef(null);

  const selected = members.find(m => m.memberId === value);
  const displayLabel = selected
    ? `${selected.memberId} — ${selected.fullName || selected.personalInfo?.fullName || selected.personalInfo?.name || ''}`
    : '';

  const inputValue = open ? query : displayLabel;

  const filtered = query.trim()
    ? members.filter(m => {
        const q = query.toLowerCase();
        const name = (m.fullName || m.personalInfo?.fullName || m.personalInfo?.name || '').toLowerCase();
        return m.memberId?.toLowerCase().includes(q) || name.includes(q);
      })
    : members;

  // Recompute fixed position each time dropdown opens or query changes
  useEffect(() => {
    if (open && inputRef.current) {
      const r = inputRef.current.getBoundingClientRect();
      // position:fixed coords are viewport-relative — do NOT add scrollY/scrollX
      setPos({ top: r.bottom, left: r.left, width: r.width });
    }
  }, [open, query]);

  // Close on outside click
  useEffect(() => {
    function onDown(e) {
      if (inputRef.current && !inputRef.current.contains(e.target)) {
        const portal = document.getElementById('msp-dropdown');
        if (!portal || !portal.contains(e.target)) {
          setOpen(false);
          setQuery('');
        }
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  function handleSelect(m) {
    onChange(m.memberId);
    setQuery('');
    setOpen(false);
  }

  return (
    <>
      <input
        ref={inputRef}
        type="text"
        placeholder="Type ID or name to search..."
        value={inputValue}
        onChange={e => { setQuery(e.target.value); setOpen(true); if (!e.target.value) onChange(''); }}
        onFocus={() => { setQuery(''); setOpen(true); }}
        autoComplete="off"
        style={{ width: '100%', padding: '6px 10px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' }}
      />
      {open && ReactDOM.createPortal(
        <div
          id="msp-dropdown"
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            width: pos.width,
            background: '#fff',
            border: '1px solid #ccc',
            borderTop: 'none',
            borderRadius: '0 0 6px 6px',
            maxHeight: '340px',
            overflowY: 'auto',
            zIndex: 999999,
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
          }}
        >
          {filtered.length === 0 ? (
            <div style={{ padding: '10px', color: '#888', fontSize: '13px' }}>No members found</div>
          ) : (
            <>
              {query.trim() === '' && (
                <div style={{ padding: '5px 12px', fontSize: '11px', color: '#aaa', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                  {filtered.length} members — type to search
                </div>
              )}
              {filtered.slice(0, query.trim() ? 200 : 50).map(m => {
                const name = m.fullName || m.personalInfo?.fullName || m.personalInfo?.name || '';
                return (
                  <div
                    key={m.id || m.memberId}
                    onMouseDown={e => { e.preventDefault(); handleSelect(m); }}
                    style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid #f5f5f5', background: m.memberId === value ? '#ede9fe' : '#fff' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f3f0ff'}
                    onMouseLeave={e => e.currentTarget.style.background = m.memberId === value ? '#ede9fe' : '#fff'}
                  >
                    <span style={{ color: '#4b3ff6', fontWeight: 600 }}>{m.memberId}</span>{' — '}{name}
                  </div>
                );
              })}
            </>
          )}
        </div>,
        document.body
      )}
    </>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

export default function ManageSharesPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Pre-fill memberId when navigating from the Left Members table
  useEffect(() => {
    const state = location.state;
    if (state?.memberId) {
      setMemberId(state.memberId);
      // Also trigger the profile load — same as selecting from the dropdown
      loadProfileByMemberId(state.memberId);
    }
  }, [location.state]);
  const [members,      setMembers]      = useState([]);
  const [memberId,     setMemberId]     = useState("");
  const [member,       setMember]       = useState(null);
  const [sharesTx,     setSharesTx]     = useState([]);
  const [changeMonth,  setChangeMonth]  = useState("");
  const [newAmount,    setNewAmount]    = useState("");
  const [showChangeForm, setShowChangeForm] = useState(false); // set true when no plan in loadProfileByMemberId
  const [extra,        setExtra]        = useState({ month: "", amount: "", method: "Bank", ref: "" });
  const [msg,          setMsg]          = useState("");
  const [err,          setErr]          = useState("");
  const [planErr,      setPlanErr]      = useState(""); // validation/API error shown inside the plan form
  const [depositErr,   setDepositErr]   = useState(""); // validation/API error shown inside the deposit form
  const [saving,       setSaving]       = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // { id, amount, description, date } | null

  useEffect(() => { loadMembers(); }, []);

  async function authedFetch(url, options = {}) {
    try {
      const endpoint = url.replace(API_URL, '');
      const method = (options.method || 'GET').toUpperCase();
      let res;

      const body = options.body ? JSON.parse(options.body) : undefined;
      if (method === 'GET')    res = await globalApi.get(endpoint);
      else if (method === 'POST')   res = await globalApi.post(endpoint, body);
      else if (method === 'PUT')    res = await globalApi.put(endpoint, body);
      else if (method === 'PATCH')  res = await globalApi.patch(endpoint, body);
      else if (method === 'DELETE') res = await globalApi.delete(endpoint);
      else                          res = await globalApi.get(endpoint);

      const text = await res.text();
      let json;
      try { json = text ? JSON.parse(text) : {}; }
      catch { json = { success: false, message: text || "Invalid JSON" }; }
      if (!res.ok) throw new Error(json?.message || `${res.status} ${res.statusText}`);
      return json;
    } catch (error) {
      if (error.message === 'Session expired') throw error;
      throw error;
    }
  }

  async function loadMembers() {
    try {
      const j = await authedFetch(`${API_URL}/reports/members-for-statement`);
      if (j?.success) setMembers(j.data?.members || []);
    } catch (e) { setErr(`Failed to load members: ${e.message}`); }
  }

  async function loadProfileByMemberId(mid) {
    setMsg(""); setErr(""); setPlanErr(""); setDepositErr("");
    try {
      const j = await authedFetch(`${API_URL}/members/by-member-id/${mid}/profile`);
      if (j?.success) {
        setMember(j.data?.member || null);
        setSharesTx(j.data?.sharesTransactions || []);
        // Pre-fill change form with current plan values
        const plan = j.data?.member?.currentBalances?.sharesPlan;
        const hasPlan = !!(plan?.monthlyAmount !== undefined && plan?.startMonth);
        setShowChangeForm(!hasPlan); // Auto-show form for members with no plan
        if (plan?.monthlyAmount !== undefined) setNewAmount(plan.monthlyAmount);
        // Default changeMonth to current month
        const now = new Date();
        setChangeMonth(`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`);
      } else setErr(j?.message || "Failed to load member profile");
    } catch (e) { setErr(`Failed to load member profile: ${e.message}`); }
  }

  async function savePlan() {
    setMsg(""); setErr(""); setPlanErr("");
    if (!memberId || !changeMonth || newAmount === "" || newAmount === null || newAmount === undefined) {
      setPlanErr("Member, Effective From month and New Monthly Amount are required.");
      return;
    }
    if (isNaN(Number(newAmount)) || Number(newAmount) < 0) {
      setPlanErr("Monthly contribution amount cannot be negative.");
      return;
    }
    setSaving(true);
    try {
      const j = await authedFetch(`${API_URL}/members/by-member-id/${memberId}/shares/plan`, {
        method: "POST",
        body: JSON.stringify({ startMonth: changeMonth, monthlyAmount: Number(newAmount) }),
      });
      if (j?.success) {
        setMsg(`✅ Contribution rate updated to KES ${fmt(newAmount, 0)}/month from ${fmtMonth(changeMonth)}.`);
        writeActivityLog({
          source: 'manage_shares_plan',
          memberId,
          memberName: member?.personalInfo?.fullName || member?.personalInfo?.name || memberId,
          startMonth: changeMonth,
          monthlyAmount: Number(newAmount),
          oldMonthlyAmount: j.data?.oldMonthlyAmount ?? null,
          oldStartMonth: j.data?.oldStartMonth ?? null,
          isNewPlan: j.data?.oldMonthlyAmount === null,
        });
        await loadProfileByMemberId(memberId);
      } else setPlanErr(j?.message || "Could not save plan");
    } catch (e) { setPlanErr(`Could not save plan: ${e.message}`); }
    finally { setSaving(false); }
  }

  async function addDeposit() {
    setMsg(""); setErr(""); setDepositErr("");
    if (extra.amount === "" || extra.amount === null || extra.amount === undefined || isNaN(Number(extra.amount))) {
      setDepositErr("Member, Month and Amount are required to add a deposit.");
      return;
    }
    if (Number(extra.amount) < 0) {
      setDepositErr("❌ Deposit amount cannot be negative.");
      return;
    }
    if (!memberId || !extra.month) {
      setDepositErr("Member, Month and Amount are required to add a deposit.");
      return;
    }
    try {
      const j = await authedFetch(`${API_URL}/members/by-member-id/${memberId}/shares`, {
        method: "POST",
        body: JSON.stringify({
          amount: Number(extra.amount),
          description: `Additional deposit for ${extra.month}`,
          paymentMethod: extra.method,
          reference: extra.ref,
          dateMonth: extra.month,
        }),
      });
      if (j?.success) {
        setMsg("✅ Deposit added successfully.");
        writeActivityLog({
          source: 'manage_shares_deposit',
          memberId,
          memberName: member?.personalInfo?.fullName || member?.personalInfo?.name || memberId,
          depositMonth: extra.month,
          amount: Number(extra.amount),
          method: extra.method,
          reference: extra.ref,
        });
        setExtra({ month: "", amount: "", method: "Bank", ref: "" });
        await loadProfileByMemberId(memberId);
      } else setDepositErr(j?.message || "Could not add deposit");
    } catch (e) { setDepositErr(`Could not add deposit: ${e.message}`); }
  }

  async function deleteDeposit(shareId) {
    setMsg(""); setErr("");
    try {
      const j = await authedFetch(`${API_URL}/members/by-member-id/${memberId}/shares/${shareId}`, {
        method: "DELETE",
      });
      if (j?.success) {
        setMsg(`✅ Record deleted successfully (KES ${fmt(j.data?.amount, 0)} — ${j.data?.description || ''}).`);
        writeActivityLog({
          source: 'manage_shares_delete',
          memberId,
          memberName: member?.personalInfo?.fullName || member?.personalInfo?.name || memberId,
          deletedShareId: shareId,
          amount: j.data?.amount,
          description: j.data?.description,
        });
        setConfirmDelete(null);
        await loadProfileByMemberId(memberId);
      } else setErr(j?.message || "Could not delete record");
    } catch (e) { setErr(`Could not delete record: ${e.message}`); }
  }

  // ── Helpers ──────────────────────────────────────────────────────────────
  const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  function fmtMonth(yyyyMm) {
    if (!yyyyMm) return '—';
    const [y, m] = yyyyMm.split('-').map(Number);
    return `${MONTH_NAMES[m-1]} ${y}`;
  }

  // Derive plan rate periods from Monthly Shares Contribution transactions
  function derivePlanPeriods(txns) {
    const planTx = (txns || [])
      .filter(t => t.description === 'Monthly Shares Contribution')
      .sort((a, b) => (a.date || '').localeCompare(b.date || ''));

    if (planTx.length === 0) return [];

    const periods = [];
    let cur = { startMonth: planTx[0].date?.slice(0,7) || '', amount: planTx[0].amount, count: 1, endMonth: planTx[0].date?.slice(0,7) || '' };

    for (let i = 1; i < planTx.length; i++) {
      const t = planTx[i];
      if (t.amount === cur.amount) {
        cur.count++;
        cur.endMonth = t.date?.slice(0,7) || cur.endMonth;
      } else {
        periods.push({ ...cur });
        cur = { startMonth: t.date?.slice(0,7) || '', amount: t.amount, count: 1, endMonth: t.date?.slice(0,7) || '' };
      }
    }
    periods.push(cur);
    return periods;
  }

  // Count months that will be updated when changing from changeMonth onwards
  function countMonthsAffected(from) {
    if (!from) return 0;
    const now = new Date();
    const [fy, fm] = from.split('-').map(Number);
    const cy = now.getFullYear(), cm = now.getMonth() + 1;
    return Math.max(0, (cy - fy) * 12 + (cm - fm) + 1);
  }

  const initialShares  = Number(member?.currentBalances?.initialShares || 0);
  // Total shares = closing balance of the LAST COMPLETED month only.
  // Backfill pre-creates plan records for current + next month in Firebase,
  // so summing all transactions would include future months and overstate the balance.
  const _now = new Date();
  const _prevMo = new Date(_now.getFullYear(), _now.getMonth() - 1, 1);
  const _prevMonthKey = `${_prevMo.getFullYear()}-${String(_prevMo.getMonth() + 1).padStart(2, '0')}`;
  const totalShares    = initialShares + (sharesTx || [])
    .filter(t => t.date && t.date.slice(0, 7) <= _prevMonthKey)
    .reduce((s, t) => s + Number(t?.amount || 0), 0);
  const currentPlan    = member?.currentBalances?.sharesPlan;
  const planPeriods    = derivePlanPeriods(sharesTx);
  const monthsAffected = countMonthsAffected(changeMonth);

  return (
    <AdminLayout
      activePage="manage-shares"
      pageTitle="Manage Member Shares"
      breadcrumbs={[
        { label: "Dashboard", link: "/admin/dashboard" },
        { label: "Members", link: "/admin/members" },
        { label: "Manage Shares" },
      ]}
    >
      <div className="reports-page">
        {err && <div className="alert alert-error">{err}</div>}
        {msg && <div className="alert alert-success">{msg}</div>}

        {/* ── Monthly Contribution Plan Card ── */}
        <div className="card">
          <div className="card-header">
            <div className="report-title-row">
              <span className="card-title">Monthly Contribution Plan</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-outline btn-sm" onClick={() => navigate('/admin/reports/merged-statement')}>
                  View Statement
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/admin/activity-log')}
                  style={{ padding: '6px 14px', fontSize: '13px', background: '#4b3ff6', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                >
                  📋 Log
                </button>
              </div>
            </div>
          </div>

          <div className="card-body">
            {/* Member selector */}
            <div className="form-grid" style={{ marginBottom: 20 }}>
              <div className="form-group" style={{ gridColumn: '1 / -1', maxWidth: 320, overflow: 'visible', position: 'relative', zIndex: 200 }}>
                <label>Member *</label>
                <MemberSearch
                  members={members}
                  value={memberId}
                  onChange={(mid) => {
                    setMemberId(mid);
                    if (mid) loadProfileByMemberId(mid);
                    else { setMember(null); setSharesTx([]); setNewAmount(""); setShowChangeForm(false); }
                  }}
                />
              </div>
            </div>

            {member && (
              <>
                {member.saccoInfo?.status === 'Left' && (
                  <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8, padding: '14px 18px', marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <span style={{ fontSize: 22, flexShrink: 0 }}>🗂️</span>
                    <div>
                      <strong style={{ color: '#92400e', fontSize: 15 }}>Archived Member — Left SACCO</strong>
                      {member.saccoInfo?.leftDate && (
                        <span style={{ color: '#78350f', fontSize: 13, marginLeft: 10 }}>Left on {(() => { const ld = member.saccoInfo.leftDate; if (!ld) return ''; const secs = ld._seconds || ld.seconds; const dt = secs ? new Date(secs * 1000) : new Date(ld); return isNaN(dt.getTime()) ? '' : dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); })()}</span>
                      )}
                      <div style={{ fontSize: 13, color: '#92400e', marginTop: 4 }}>Share plan has been stopped. No further contributions or edits are permitted.</div>
                    </div>
                  </div>
                )}
                {/* ── Summary bar ── */}
                <div className="compact-summary" style={{ marginBottom: 20 }}>
                  <div className="compact-summary-item">
                    <span className="compact-label">Total Shares (as of {fmtMonth(_prevMonthKey)}):</span>
                    <span className="compact-value">{fmt(totalShares, 0)}</span>
                  </div>
                  <div className="compact-summary-item">
                    <span className="compact-label">Current Rate:</span>
                    <span className="compact-value">
                      {currentPlan?.monthlyAmount ? `KES ${fmt(currentPlan.monthlyAmount, 0)}/month` : 'No plan set'}
                      {currentPlan?.startMonth ? <span style={{ fontSize: 12, color: '#888', marginLeft: 6 }}>(since {fmtMonth(currentPlan.startMonth)})</span> : null}
                    </span>
                  </div>
                  <div className="compact-summary-item highlight">
                    <span className="compact-label">3× Borrowing Cap:</span>
                    <span className="compact-value text-success">{fmt(totalShares * 3, 0)}</span>
                  </div>
                </div>

                {/* ── Monthly Contribution Schedule — full ledger from plan start to next month ── */}
                {(currentPlan?.startMonth || sharesTx.some(t => t.description === 'Monthly Shares Contribution')) && (() => {
                  // Build complete month list: earliest actual record (or plan start) → next month
                  const schedule = [];
                  const planTxMonths = sharesTx
                    .filter(t => t.description === 'Monthly Shares Contribution' && t.date)
                    .map(t => t.date.slice(0,7))
                    .sort();
                  const effectiveStart = planTxMonths.length > 0
                    ? planTxMonths[0]
                    : (currentPlan?.startMonth || '');
                  const [sy, sm] = effectiveStart.split('-').map(Number);
                  const now = new Date();
                  // Show up to next month
                  let endY = now.getFullYear(), endM = now.getMonth() + 2; // +2 = next month
                  if (endM > 12) { endM = 1; endY++; }

                  // Build map of actual plan records: YYYY-MM -> plan amount
                  const actualMap = {};
                  sharesTx
                    .filter(t => t.description === 'Monthly Shares Contribution')
                    .forEach(t => { if (t.date) actualMap[t.date.slice(0,7)] = t.amount; });

                  // Build map of ALL transactions per month (plan + deposits): YYYY-MM -> total
                  const allMonthTxSumMap = {};
                  sharesTx.forEach(t => {
                    const mk2 = t.date?.slice(0, 7);
                    if (mk2) allMonthTxSumMap[mk2] = (allMonthTxSumMap[mk2] || 0) + Number(t.amount || 0);
                  });

                  // Build map: monthKey -> last newBalance in that month (already computed by backend)
                  const monthEndBalanceMap = {};
                  [...sharesTx]
                    .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
                    .forEach(t => {
                      const mk2 = t.date?.slice(0, 7);
                      if (mk2 && t.newBalance !== undefined) monthEndBalanceMap[mk2] = t.newBalance;
                    });

                  // Starting balance = last known balance before schedule start
                  const beforeStartMonths = Object.keys(monthEndBalanceMap)
                    .filter(m => m < effectiveStart).sort();
                  let projectedBalance = beforeStartMonths.length > 0
                    ? monthEndBalanceMap[beforeStartMonths[beforeStartMonths.length - 1]]
                    : 0;

                  let cy = sy, cm = sm;
                  while (cy < endY || (cy === endY && cm <= endM)) {
                    const mk = `${cy}-${String(cm).padStart(2,'0')}`;
                    const isNextMonth = cy === endY && cm === endM;
                    const isCurrentMonth = cy === now.getFullYear() && cm === now.getMonth() + 1;
                    const amount = allMonthTxSumMap[mk] !== undefined ? allMonthTxSumMap[mk] : (currentPlan?.monthlyAmount || 0);
                    const hasRecord = actualMap[mk] !== undefined;
                    // Use actual end-of-month balance for recorded months; project forward for unrecorded
                    let runningTotal;
                    if (monthEndBalanceMap[mk] !== undefined) {
                      runningTotal = monthEndBalanceMap[mk];
                      projectedBalance = runningTotal;
                    } else {
                      projectedBalance += amount;
                      runningTotal = projectedBalance;
                    }
                    schedule.push({ mk, amount, isCurrentMonth, isNextMonth, hasRecord, runningTotal });
                    cm++;
                    if (cm > 12) { cm = 1; cy++; }
                  }

                  return (
                    <div style={{ marginBottom: 24, borderRadius: 8, border: '1px solid #e0e0e0', overflow: 'visible' }}>
                      <div style={{ padding: '10px 16px', background: '#f0f0ff', borderBottom: '1px solid #e0e0e0', fontWeight: 700, fontSize: 13, color: '#4b3ff6' }}>
                        📅 Monthly Contribution Schedule
                        <span style={{ fontWeight: 400, color: '#888', marginLeft: 8, fontSize: 12 }}>
                          {currentPlan?.startMonth ? fmtMonth(currentPlan.startMonth) : 'Historical records'} → present
                        </span>
                      </div>
                      <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                          <thead style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>
                            <tr>
                              <th style={{ padding: '8px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0', color: '#555' }}>Month</th>
                              <th style={{ padding: '8px 16px', textAlign: 'right', borderBottom: '1px solid #e0e0e0', color: '#555' }}>Monthly Amount (KES)</th>
                              <th style={{ padding: '8px 16px', textAlign: 'right', borderBottom: '1px solid #e0e0e0', color: '#555' }}>Running Total (KES)</th>
                              <th style={{ padding: '8px 16px', textAlign: 'center', borderBottom: '1px solid #e0e0e0', color: '#555' }}>Record</th>
                              <th style={{ padding: '8px 16px', textAlign: 'center', borderBottom: '1px solid #e0e0e0', color: '#555' }}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {schedule.map(({ mk, amount, isCurrentMonth, isNextMonth, hasRecord, runningTotal }) => {
                              const bg = isCurrentMonth ? '#f0f0ff' : isNextMonth ? '#f0fdf4' : 'white';
                              return (
                                <tr key={mk}
                                  style={{ background: bg }}
                                  onMouseEnter={e => e.currentTarget.style.background = '#e8f0fe'}
                                  onMouseLeave={e => e.currentTarget.style.background = bg}
                                >
                                  <td style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0', fontWeight: (isCurrentMonth || isNextMonth) ? 700 : 400 }}>
                                    {fmtMonth(mk)}
                                    {isCurrentMonth && <span style={{ marginLeft: 8, fontSize: 11, background: '#4b3ff6', color: 'white', padding: '1px 7px', borderRadius: 10 }}>This month</span>}
                                    {isNextMonth && <span style={{ marginLeft: 8, fontSize: 11, background: '#16a34a', color: 'white', padding: '1px 7px', borderRadius: 10 }}>Next month</span>}
                                  </td>
                                  <td style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0', textAlign: 'right', fontWeight: 700, color: isCurrentMonth ? '#4b3ff6' : '#333' }}>
                                    {fmt(amount, 0)}
                                  </td>
                                  <td style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0', textAlign: 'right', fontWeight: 700, color: isCurrentMonth ? '#4b3ff6' : isNextMonth ? '#16a34a' : '#333' }}>
                                    {fmt(runningTotal, 0)}
                                  </td>
                                  <td style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}>
                                    {hasRecord
                                      ? <span style={{ fontSize: 11, color: '#16a34a' }}>✅ Recorded</span>
                                      : <span style={{ fontSize: 11, color: '#ca8a04' }}>⚡ Projected</span>
                                    }
                                  </td>
                                  <td style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}>
                                    <button
                                      onClick={() => { setChangeMonth(mk); setNewAmount(amount); setShowChangeForm(true); setTimeout(() => document.getElementById('change-rate-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100); }}
                                      style={{ fontSize: 11, padding: '2px 10px', background: '#e0e7ff', color: '#4b3ff6', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 600 }}
                                      title={`Edit rate from ${fmtMonth(mk)}`}
                                    >
                                      ✏️ Edit
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}

                {/* ── Change Rate Form — shown only when Edit is clicked ── */}
                {(showChangeForm || !currentPlan?.startMonth) && (
                <div id="change-rate-form" style={{ padding: '16px 18px', background: '#fff9f0', borderRadius: 8, border: '1px solid #fde68a' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#92400e' }}>
                      {currentPlan?.startMonth ? '✏️ Change Monthly Contribution Rate' : '🆕 Set Up Monthly Contribution Plan'}
                    </div>
                    {currentPlan?.startMonth && (
                      <button onClick={() => setShowChangeForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#888' }} title="Close">✕</button>
                    )}
                  </div>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>New Monthly Amount (KES) *</label>
                      <input
                        type="number"
                        value={newAmount}
                        onChange={e => { setNewAmount(e.target.value); setPlanErr(""); }}
                        placeholder="0"
                        min="0"
                      />
                    </div>
                    <div className="form-group">
                      <label>Effective From *
                        <span style={{ fontWeight: 400, fontSize: 12, color: '#888', marginLeft: 6 }}>
                          (applies from this month onwards)
                        </span>
                      </label>
                      <input type="month" value={changeMonth} onChange={e => setChangeMonth(e.target.value)} />
                    </div>
                    <div className="form-group form-group-action">
                      <label style={{ visibility: 'hidden' }}>.</label>
                      <button className="btn btn-primary" onClick={savePlan} disabled={saving}>
                        {saving ? "Saving..." : (currentPlan?.startMonth ? "Save New Plan" : "Create Plan")}
                      </button>
                    </div>
                  </div>
                  {planErr && (
                    <div className="alert alert-error" style={{ marginTop: 10, marginBottom: 0 }}>{planErr}</div>
                  )}
                  {changeMonth && newAmount !== "" && newAmount !== null && newAmount !== undefined && (
                    <div style={{ marginTop: 8, fontSize: 13, color: '#555' }}>
                      💡 This will set contributions to <strong>KES {fmt(newAmount, 0)}/month</strong> for{' '}
                      <strong>{monthsAffected} month{monthsAffected !== 1 ? 's' : ''}</strong>{' '}
                      from <strong>{fmtMonth(changeMonth)}</strong> to present.
                      {currentPlan?.startMonth && changeMonth > currentPlan.startMonth
                        ? <span style={{ color: '#16a34a' }}> Earlier months (before {fmtMonth(changeMonth)}) are unchanged.</span>
                        : null}
                    </div>
                  )}
                </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── Additional Deposit Card — hidden while editing an existing plan's rate ── */}
        {member && !(showChangeForm && currentPlan?.startMonth) && (
          <div className="card">
            <div className="card-header">
              <span className="card-title">Additional Deposit</span>
            </div>
            <div className="card-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Deposit Month *</label>
                  <input type="month" value={extra.month} onChange={e => setExtra({ ...extra, month: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Amount (KES) *</label>
                  <input type="number" value={extra.amount} onChange={e => { setExtra({ ...extra, amount: e.target.value }); setDepositErr(""); }} placeholder="0" min="0" />
                </div>
                <div className="form-group">
                  <label>Method</label>
                  <select value={extra.method} onChange={e => setExtra({ ...extra, method: e.target.value })}>
                    <option>Bank</option>
                    <option>Mpesa</option>
                    <option>Cash</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Reference</label>
                  <input type="text" value={extra.ref} onChange={e => setExtra({ ...extra, ref: e.target.value })} placeholder="Receipt / Ref" />
                </div>
                <div className="form-group form-group-action">
                  <button className="btn btn-secondary" onClick={addDeposit} disabled={member?.saccoInfo?.status === 'Left'} style={member?.saccoInfo?.status === 'Left' ? { opacity: 0.4, cursor: 'not-allowed' } : {}}>Add Deposit</button>
                </div>
              </div>
              {depositErr && (
                <div className="alert alert-error" style={{ marginTop: 12, marginBottom: 0 }}>{depositErr}</div>
              )}
            </div>
          </div>
        )}

        {/* ── Transaction History Card ── */}
        {sharesTx?.length > 0 && (
          <div className="card">
            <div className="card-header">
              <span className="card-title">Transaction History</span>
              <span style={{ fontSize: 12, color: '#888', marginLeft: 8 }}>
                Deposits can be deleted — plan contributions cannot
              </span>
            </div>
            <div className="card-body">

              {/* ── Inline delete confirmation ── */}
              {confirmDelete && (
                <div style={{
                  background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 6,
                  padding: '12px 16px', marginBottom: 14, display: 'flex',
                  alignItems: 'center', gap: 16, flexWrap: 'wrap'
                }}>
                  <span style={{ fontWeight: 600, color: '#856404' }}>⚠️ Confirm delete:</span>
                  <span style={{ color: '#555' }}>
                    <strong>{confirmDelete.date}</strong> — {confirmDelete.description} —{' '}
                    <strong style={{ color: '#dc2626' }}>KES {fmt(confirmDelete.amount, 0)}</strong>
                  </span>
                  <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => deleteDeposit(confirmDelete.id)}
                      style={{ padding: '5px 14px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
                    >
                      Yes, Delete
                    </button>
                    <button
                      onClick={() => setConfirmDelete(null)}
                      style={{ padding: '5px 14px', background: '#6b7280', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                  </span>
                </div>
              )}

              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Description</th>
                      <th className="text-right">Amount</th>
                      <th className="text-right">Balance After</th>
                      <th>Edited By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sharesTx.map(t => {
                      const isPlan    = t.description === 'Monthly Shares Contribution';
                      const isDeposit = !isPlan;
                      const isConfirming = confirmDelete?.id === t.id;
                      return (
                        <tr key={t.id} style={{
                          background: isConfirming ? '#fff3cd' : isPlan ? '#f8f9fa' : '#f0fdf4'
                        }}>
                          <td>{t.date || ""}</td>
                          <td>
                            {isPlan
                              ? <span style={{ fontSize: 12, background: '#e9ecef', padding: '2px 8px', borderRadius: 10, color: '#555' }}>📅 Plan</span>
                              : <span style={{ fontSize: 12, background: '#dcfce7', padding: '2px 8px', borderRadius: 10, color: '#166534' }}>💰 Deposit</span>
                            }
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                              <span>{t.description || ""}</span>
                              {isDeposit && (
                                <button
                                  onClick={() => setConfirmDelete(
                                    isConfirming ? null
                                    : { id: t.id, amount: t.amount, description: t.description, date: t.date }
                                  )}
                                  title="Delete this deposit"
                                  style={{
                                    flexShrink: 0, padding: '2px 10px', fontSize: 12,
                                    background: isConfirming ? '#6b7280' : '#fee2e2',
                                    color:      isConfirming ? '#fff'     : '#dc2626',
                                    border: `1px solid ${isConfirming ? '#6b7280' : '#fca5a5'}`,
                                    borderRadius: 4, cursor: 'pointer', fontWeight: 600
                                  }}
                                >
                                  {isConfirming ? '✕ Cancel' : '🗑 Delete'}
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="text-right" style={{ fontWeight: isDeposit ? 700 : 400, color: isDeposit ? '#16a34a' : 'inherit' }}>
                            {fmt(t.amount, 0)}
                          </td>
                          <td className="text-right">{fmt(t.newBalance, 0)}</td>
                          <td style={{ fontSize: 12, color: '#555' }}>
                            {t.editedByName ? `${t.editedByName} (${t.editedBy})` : t.editedBy || "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}